/**
 * Intl formatting wrappers (ADR-0025). ALL number/date formatting in the app goes through here so
 * formatted values never leak into translation catalogs and the household timezone (not the device
 * TZ) is always used for dates (ADR-0023).
 *
 * Crucially (ADR-0022, kid-safety): virtual POINTS are never formatted as currency — they are a
 * plain localized integer; the calling component appends the catalog-pluralized unit + coin icon.
 * Only REAL money (allowance/ledger) uses `style: 'currency'`.
 */

const numberFormatCache = new Map<string, Intl.NumberFormat>();

function nf(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let fmt = numberFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(key, fmt);
  }
  return fmt;
}

/**
 * Virtual points as a localized plain integer (NO currency styling). The unit word ("coins") and
 * the coin icon are the caller's responsibility via the pluralized `family:common.coinsLabel` key.
 */
export function formatPoints(locale: string, points: number): string {
  return nf(locale, { maximumFractionDigits: 0 }).format(points);
}

/**
 * Real money for ledger rows: localized currency with an explicit sign so credits/debits read
 * clearly (`+$5.00` / `-$2.50`). `currency` is the household's ISO code.
 */
export function formatMoney(
  locale: string,
  amountMinor: number,
  currency: string,
  options: { signDisplay?: Intl.NumberFormatOptions['signDisplay'] } = {},
): string {
  // Amounts are stored in minor units (cents); convert to major for display.
  const major = amountMinor / 100;
  const signDisplay = options.signDisplay ?? 'always';
  try {
    return nf(locale, { style: 'currency', currency, signDisplay }).format(major);
  } catch {
    // `currency` was not a valid ISO-4217 code (e.g. the economy's LOGICAL 'cash'/'coin', which are
    // not ISO codes). Never crash the page on a formatting bug — fall back to a plain localized 2-dp
    // number. (The correct fix is callers passing the household's real ISO currency.)
    return nf(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      signDisplay,
    }).format(major);
  }
}

const dateFormatCache = new Map<string, Intl.DateTimeFormat>();

function df(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let fmt = dateFormatCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    dateFormatCache.set(key, fmt);
  }
  return fmt;
}

/** Accepted date inputs across the formatters: a Date, a parseable string/epoch-ms, or nullish. */
export type DateInput = Date | string | number | null | undefined;

/** Matches a bare civil date with no time/zone, e.g. "2026-06-10" — a household-local day, not an instant. */
const BARE_CIVIL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Normalize to a valid Date, or null if absent/invalid — so a bad/missing value never throws in Intl. */
function toValidDate(value: DateInput): Date | null {
  if (value == null) return null;
  let input = value;
  // A bare "YYYY-MM-DD" is parsed by JS as UTC midnight; pin it to noon UTC so the instant sits safely
  // mid-day. The civil-day formatters render it in UTC (see civilTimeZone) so the authored calendar day
  // shows verbatim for EVERY household — a single instant re-projected into a household timezone cannot
  // cover the full -12..+14 offset range (noon-UTC alone still rolled +12..+14 zones forward a day).
  if (typeof input === 'string' && BARE_CIVIL_DATE.test(input)) {
    input = `${input}T12:00:00.000Z`;
  }
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The timezone a civil-day formatter should render in. A bare "YYYY-MM-DD" is a household-LOCAL calendar
 * day, not an instant — render it in UTC so its authored day shows verbatim regardless of household offset
 * (re-projecting the pinned instant into a real timezone rolls +12..+14 zones a day forward). A real
 * instant keeps the household timezone.
 */
function civilTimeZone(date: DateInput, timeZone: string): string {
  return typeof date === 'string' && BARE_CIVIL_DATE.test(date) ? 'UTC' : timeZone;
}

/** Rendered when a date is missing/invalid, so a null/garbage value degrades gracefully (never crashes). */
export const INVALID_DATE_PLACEHOLDER = '—';

/** A calendar day in the household timezone (e.g. "Tue, Jun 10"). Tolerates null/invalid input. */
export function formatDay(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  const tz = civilTimeZone(date, timeZone);
  return df(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz }).format(d);
}

/** A compact month + day in the household timezone (e.g. "Jun 10"), for dense chart axes. */
export function formatMonthDay(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  return df(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: civilTimeZone(date, timeZone),
  }).format(d);
}

/** A short weekday in the household timezone (e.g. "Mon"), for the calendar dot strip. */
export function formatWeekdayShort(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  return df(locale, { weekday: 'short', timeZone: civilTimeZone(date, timeZone) }).format(d);
}

/** The day-of-month number in the household timezone (e.g. "10"), for the calendar dot strip. */
export function formatDayOfMonth(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  return df(locale, { day: 'numeric', timeZone: civilTimeZone(date, timeZone) }).format(d);
}

/** A short month name in the household timezone (e.g. "Jun"), for calendar/heatmap column labels. */
export function formatMonthShort(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  return df(locale, { month: 'short', timeZone: civilTimeZone(date, timeZone) }).format(d);
}

/** A date+time in the household timezone. Tolerates null/invalid input. */
export function formatDateTime(locale: string, date: DateInput, timeZone: string): string {
  const d = toValidDate(date);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  return df(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(d);
}

/**
 * Whether the locale conventionally uses a 24-hour clock (uk → true, en → false). Derived from Intl so
 * it follows the platform's locale data rather than a hardcoded list.
 */
export function uses24HourClock(locale: string): boolean {
  try {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12 === false;
  } catch {
    return false;
  }
}

/**
 * A wall-clock time formatted for the locale's hour cycle, WITHOUT an am/pm marker (an analog face is
 * inherently 12-hour, so am/pm would be misleading). uk → 24-hour ("14:05", "8:00"); en → 12-hour
 * ("2:05", "8:00"). `hours` accepts a 12-hour face value (1..12) or a 24-hour value (0..23); minutes
 * 0..59. The hour is non-padded and the minute zero-padded, matching the in-exercise "{h}:{mm}" style.
 */
export function formatClockTime(locale: string, hours: number, minutes: number): string {
  const h24 = ((Math.round(hours) % 24) + 24) % 24;
  const m = ((Math.round(minutes) % 60) + 60) % 60;
  const h = uses24HourClock(locale) ? h24 : h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${String(m).padStart(2, '0')}`;
}

const relativeFormatCache = new Map<string, Intl.RelativeTimeFormat>();

/** Relative time ("in 2 days", "3 hours ago"). Picks the largest sensible unit. Tolerates null/invalid. */
export function formatRelative(locale: string, target: DateInput, now: Date = new Date()): string {
  const d = toValidDate(target);
  if (!d) return INVALID_DATE_PLACEHOLDER;
  let fmt = relativeFormatCache.get(locale);
  if (!fmt) {
    fmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    relativeFormatCache.set(locale, fmt);
  }
  const deltaSec = Math.round((d.getTime() - now.getTime()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
    ['second', 1],
  ];
  for (let i = 0; i < units.length; i++) {
    const [unit, secs] = units[i] as [Intl.RelativeTimeFormatUnit, number];
    if (Math.abs(deltaSec) >= secs || unit === 'second') {
      const value = Math.round(deltaSec / secs);
      // Rounding can tip the count into the next-larger unit's range (e.g. 3599s rounds to 60 minutes,
      // 86399s to 24 hours) — promote to the larger unit instead of formatting the overflowed count.
      if (i > 0) {
        const [largerUnit, largerSecs] = units[i - 1] as [Intl.RelativeTimeFormatUnit, number];
        const ratio = Math.round(largerSecs / secs);
        if (Math.abs(value) >= ratio) {
          return fmt.format(Math.sign(value) * Math.round(Math.abs(value) / ratio), largerUnit);
        }
      }
      return fmt.format(value, unit);
    }
  }
  return fmt.format(0, 'second');
}

/** Small fallback map for first-day-of-week when `Intl.Locale` week info is unavailable. */
const FIRST_DAY_FALLBACK: Record<string, number> = { en: 7, uk: 1 };

/**
 * Languages whose audience lives (for this product) in a single week-convention region, pinned so a
 * device's OS region can never override the family's convention: a Ukrainian family on an `en-US`
 * macOS still gets a Monday week. Ambiguous languages (`en` spans Sunday- and Monday-week regions)
 * are deliberately NOT pinned — they resolve via the device's region below.
 */
const FIRST_DAY_BY_LANGUAGE: Record<string, number> = { uk: 1 };

/** The region subtag of a BCP-47 tag (`en-UA` → `UA`), or undefined when absent/invalid. */
function localeRegion(tag: string): string | undefined {
  try {
    return new Intl.Locale(tag).region ?? undefined;
  } catch {
    return undefined;
  }
}

/** CLDR first day (1=Mon..7=Sun) for a tag via `getWeekInfo()` (Chrome/Node) / `weekInfo` (Safari). */
function weekInfoFirstDay(tag: string): number | undefined {
  try {
    const loc = new Intl.Locale(tag) as {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    return loc.getWeekInfo?.()?.firstDay ?? loc.weekInfo?.firstDay;
  } catch {
    return undefined;
  }
}

/**
 * First day of week for a locale (1 = Monday … 7 = Sunday, CLDR). Uses `Intl.Locale` week info
 * where supported, with a small fallback map; a parent override can supersede this at the call site.
 *
 * The app stores languages as REGION-LESS tags (`en`, `uk`), and CLDR resolves a bare language to
 * its LIKELY region — `en` → `en-US` → Sunday — which is wrong for, say, an English-speaking family
 * in Ukraine (Monday-week, like most of Europe). Resolution order:
 *   1. an explicit region in the tag (`en-UA`) — week conventions are regional in CLDR;
 *   2. the language pin (`uk` → Monday) — never overridden by a device's OS region;
 *   3. the language + the DEVICE's region (`en` on an `en-GB`/`uk-UA` device → Monday) — the device
 *      region reflects where the family actually lives, the app language only what they read;
 *   4. the bare tag's CLDR likely region, then the static fallback map.
 */
export function firstDayOfWeek(locale: string): number {
  const language = locale.split('-')[0] ?? locale;
  const candidates: string[] = [];
  if (localeRegion(locale)) {
    candidates.push(locale);
  } else {
    const pinned = FIRST_DAY_BY_LANGUAGE[language];
    if (pinned) return pinned;
    const device =
      (typeof navigator !== 'undefined' && navigator.language) ||
      Intl.DateTimeFormat().resolvedOptions().locale;
    const deviceRegion = localeRegion(device);
    if (deviceRegion) candidates.push(`${locale}-${deviceRegion}`);
    candidates.push(locale);
  }
  for (const tag of candidates) {
    const firstDay = weekInfoFirstDay(tag);
    if (firstDay) return firstDay;
  }
  return FIRST_DAY_FALLBACK[language] ?? 1;
}

/* ── Grammatical number agreement (ADR-0057 §4.1) ───────────────────────────────────────────────────── */

/** CLDR plural forms for a noun. `one`/`other` are universal; `few`/`many` are Slavic (uk). */
export interface PluralForms {
  readonly one: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

const pluralRulesCache = new Map<string, Intl.PluralRules>();
function pluralRules(locale: string): Intl.PluralRules {
  let rules = pluralRulesCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRulesCache.set(locale, rules);
  }
  return rules;
}

/**
 * Select the CLDR-agreeing form of a noun for a count. Ukrainian needs three forms (1 яблуко / 2 яблука /
 * 5 яблук — `Intl.PluralRules` knows the mod-10/100 rules); English needs one/other. Falls back to `other`
 * when a category's form is absent.
 */
export function pluralForm(locale: string, count: number, forms: PluralForms): string {
  const category = pluralRules(locale).select(count);
  return (forms as unknown as Record<string, string | undefined>)[category] ?? forms.other;
}

/** The quantified phrase "{count} {agreeing noun}" (ADR-0057 §4.1) — the fix for "7 яблоко". The numeral is
 *  locale-formatted (digit grouping/separators) like every other number in the app; the RAW count drives the
 *  plural-category selection. */
export function quantify(locale: string, count: number, forms: PluralForms): string {
  return `${nf(locale, { maximumFractionDigits: 0 }).format(count)} ${pluralForm(locale, count, forms)}`;
}
